import type {
  FbaCalculationInput,
  FbaCalculationResult,
  SizeTier,
  PriceTier,
  ProductType
} from '@/types';

// 常量定义
export const CONVERSION_RATES = {
  CM_TO_IN: 0.393701,
  GRAM_TO_LB: 1 / 453.59237,
  GRAM_TO_KG: 1 / 1000,
  OZ_TO_LB: 1 / 16,
  LB_TO_KG: 0.45359237,
  KG_TO_LB: 1 / 0.45359237
} as const;

// 2026年FBA费用表 - 非旺季服装类商品除外
const FBA_FEES_2026_NON_PEAK = {
  // 小号标准尺寸
  'small-standard': {
    // 价格分段: <$10, $10-$50, >$50
    tiers: {
      low: [
        { maxWeight: 0.125, fee: 2.43 },    // 不超过2盎司
        { maxWeight: 0.25, fee: 2.49 },     // 2至4盎司
        { maxWeight: 0.375, fee: 2.56 },    // 4至6盎司
        { maxWeight: 0.5, fee: 2.66 },      // 6至8盎司
        { maxWeight: 0.625, fee: 2.77 },    // 8至10盎司
        { maxWeight: 0.75, fee: 2.82 },     // 10至12盎司
        { maxWeight: 0.875, fee: 2.92 },    // 12至14盎司
        { maxWeight: 1.0, fee: 2.95 }       // 14至16盎司
      ],
      medium: [
        { maxWeight: 0.125, fee: 3.32 },
        { maxWeight: 0.25, fee: 3.42 },
        { maxWeight: 0.375, fee: 3.45 },
        { maxWeight: 0.5, fee: 3.54 },
        { maxWeight: 0.625, fee: 3.68 },
        { maxWeight: 0.75, fee: 3.78 },
        { maxWeight: 0.875, fee: 3.91 },
        { maxWeight: 1.0, fee: 3.96 }
      ],
      high: [
        { maxWeight: 0.125, fee: 3.58 },
        { maxWeight: 0.25, fee: 3.68 },
        { maxWeight: 0.375, fee: 3.71 },
        { maxWeight: 0.5, fee: 3.80 },
        { maxWeight: 0.625, fee: 3.94 },
        { maxWeight: 0.75, fee: 4.04 },
        { maxWeight: 0.875, fee: 4.17 },
        { maxWeight: 1.0, fee: 4.22 }
      ]
    }
  },
  // 大号标准尺寸
  'large-standard': {
    tiers: {
      low: [
        { maxWeight: 0.25, fee: 2.91 },     // 不超过4盎司
        { maxWeight: 0.5, fee: 3.13 },      // 4至8盎司
        { maxWeight: 0.75, fee: 3.38 },      // 8至12盎司
        { maxWeight: 1.0, fee: 3.78 },       // 12至16盎司
        { maxWeight: 1.25, fee: 4.22 },      // 1至1.25磅
        { maxWeight: 1.5, fee: 4.60 },       // 1.25至1.5磅
        { maxWeight: 1.75, fee: 4.75 },      // 1.5至1.75磅
        { maxWeight: 2.0, fee: 5.00 },       // 1.75至2磅
        { maxWeight: 2.25, fee: 5.10 },      // 2至2.25磅
        { maxWeight: 2.5, fee: 5.28 },       // 2.25至2.5磅
        { maxWeight: 2.75, fee: 5.44 },      // 2.5至2.75磅
        { maxWeight: 3.0, fee: 5.85 }        // 2.75至3磅
      ],
      medium: [
        { maxWeight: 0.25, fee: 3.73 },
        { maxWeight: 0.5, fee: 3.95 },
        { maxWeight: 0.75, fee: 4.20 },
        { maxWeight: 1.0, fee: 4.60 },
        { maxWeight: 1.25, fee: 5.04 },
        { maxWeight: 1.5, fee: 5.42 },
        { maxWeight: 1.75, fee: 5.57 },
        { maxWeight: 2.0, fee: 5.82 },
        { maxWeight: 2.25, fee: 5.92 },
        { maxWeight: 2.5, fee: 6.10 },
        { maxWeight: 2.75, fee: 6.26 },
        { maxWeight: 3.0, fee: 6.97 }
      ],
      high: [
        { maxWeight: 0.25, fee: 3.99 },
        { maxWeight: 0.5, fee: 4.21 },
        { maxWeight: 0.75, fee: 4.46 },
        { maxWeight: 1.0, fee: 4.86 },
        { maxWeight: 1.25, fee: 5.30 },
        { maxWeight: 1.5, fee: 5.68 },
        { maxWeight: 1.75, fee: 5.83 },
        { maxWeight: 2.0, fee: 6.08 },
        { maxWeight: 2.25, fee: 6.18 },
        { maxWeight: 2.5, fee: 6.36 },
        { maxWeight: 2.75, fee: 6.52 },
        { maxWeight: 3.0, fee: 6.93 }
      ]
    }
  },
  // 小号大件
  'small-oversize': {
    tiers: {
      low: [ { maxWeight: 50, fee: 6.78, additionalPerLb: 0.38 } ],
      medium: [ { maxWeight: 50, fee: 7.55, additionalPerLb: 0.38 } ],
      high: [ { maxWeight: 50, fee: 7.55, additionalPerLb: 0.38 } ]
    }
  },
  // 大号大件
  'large-oversize': {
    tiers: {
      low: [ { maxWeight: 50, fee: 8.58, additionalPerLb: 0.38 } ],
      medium: [ { maxWeight: 50, fee: 9.35, additionalPerLb: 0.38 } ],
      high: [ { maxWeight: 50, fee: 9.35, additionalPerLb: 0.38 } ]
    }
  },
  // 特殊超大尺寸 - 分段计费 (2026年费率)
  'special-oversize': {
    tiers: {
      low: [
        // 0-50磅段
        { maxWeight: 50, fee: 25.56, weightSegment: '0-50', additionalPerLb: 0.38 },
        // 50-70磅段 (比2025年降价$2.80)
        { maxWeight: 70, fee: 36.55, weightSegment: '50-70', additionalPerLb: 0.75, baseWeight: 51 },
        // 70-150磅段 (比2025年降价$3.49)
        { maxWeight: 150, fee: 50.55, weightSegment: '70-150', additionalPerLb: 0.75, baseWeight: 71 },
        // 超过150磅段
        { maxWeight: Infinity, fee: 194.18, weightSegment: 'over-150', additionalPerLb: 0.19, baseWeight: 151 }
      ],
      medium: [
        // 0-50磅段
        { maxWeight: 50, fee: 26.33, weightSegment: '0-50', additionalPerLb: 0.38 },
        // 50-70磅段 (比2025年降价$2.80)
        { maxWeight: 70, fee: 37.32, weightSegment: '50-70', additionalPerLb: 0.75, baseWeight: 51 },
        // 70-150磅段 (比2025年降价$3.49)
        { maxWeight: 150, fee: 51.32, weightSegment: '70-150', additionalPerLb: 0.75, baseWeight: 71 },
        // 超过150磅段
        { maxWeight: Infinity, fee: 194.95, weightSegment: 'over-150', additionalPerLb: 0.19, baseWeight: 151 }
      ],
      high: [
        // 0-50磅段
        { maxWeight: 50, fee: 26.33, weightSegment: '0-50', additionalPerLb: 0.38 },
        // 50-70磅段 (比2025年降价$2.80)
        { maxWeight: 70, fee: 37.32, weightSegment: '50-70', additionalPerLb: 0.75, baseWeight: 51 },
        // 70-150磅段 (比2025年降价$3.49)
        { maxWeight: 150, fee: 51.32, weightSegment: '70-150', additionalPerLb: 0.75, baseWeight: 71 },
        // 超过150磅段
        { maxWeight: Infinity, fee: 194.95, weightSegment: 'over-150', additionalPerLb: 0.19, baseWeight: 151 }
      ]
    }
  }
};

// 服装类商品费用调整因子
const APPAREL_ADJUSTMENTS = {
  'small-standard': 1.08,  // 小标准尺寸增加8%
  'large-standard': 1.09,  // 大标准尺寸增加9%
  'small-oversize': 1.00,  // 小大件无变化
  'large-oversize': 1.00,  // 大大件无变化
  'special-oversize': 1.00 // 特殊超大件无变化
};

// 工具函数
export class FbaCalculator {
  /**
   * 转换尺寸到英寸
   */
  static convertDimensionsToInches(dimensions: any): {
    length: number;
    width: number;
    height: number;
  } {
    const { length, width, height, unit } = dimensions;

    if (unit === 'metric') {
      return {
        length: length * CONVERSION_RATES.CM_TO_IN,
        width: width * CONVERSION_RATES.CM_TO_IN,
        height: height * CONVERSION_RATES.CM_TO_IN
      };
    }

    return { length, width, height };
  }

  /**
   * 转换重量到磅
   */
  static convertWeightToLbs(weight: any): number {
    const { value, unit } = weight;

    switch (unit) {
      case 'kg':
        return value * CONVERSION_RATES.KG_TO_LB;
      case 'g':
        return value * CONVERSION_RATES.GRAM_TO_LB;
      case 'oz':
        return value * CONVERSION_RATES.OZ_TO_LB;
      case 'lb':
        return value;
      default:
        return value;
    }
  }

  /**
   * 确定尺寸分段 - Amazon FBA官方规则
   */
  static determineSizeTier(length: number, width: number, height: number, weightLbs: number): SizeTier {
    // 按尺寸排序
    const sorted = [length, width, height].sort((a, b) => b - a);
    const [longest, median, shortest] = sorted;

    // 计算长度+围长
    const lengthPlusGirth = longest + 2 * (median + shortest);

    // 小号标准尺寸 (Small Standard)
    // 重量限制: ≤ 16盎司 (1磅)
    // 尺寸限制: 最长边 ≤ 15英寸，中等边 ≤ 12英寸，最短边 ≤ 0.75英寸
    if (longest <= 15 && median <= 12 && shortest <= 0.75 && weightLbs <= 1) {
      return 'small-standard';
    }

    // 大号标准尺寸 (Large Standard)
    // 重量限制: ≤ 20磅
    // 尺寸限制: 最长边 ≤ 18英寸，中等边 ≤ 14英寸，最短边 ≤ 8英寸
    if (longest <= 18 && median <= 14 && shortest <= 8 && weightLbs <= 20) {
      return 'large-standard';
    }

    // 小号大件 (Small Oversize)
    // 重量限制: ≤ 50磅
    // 尺寸限制: 最长边 ≤ 59英寸，中等边 + 最短边 ≤ 33英寸
    if (longest <= 59 && (median + shortest) <= 33 && weightLbs <= 50) {
      return 'small-oversize';
    }

    // 大号大件 (Large Oversize)
    // 重量限制: ≤ 50磅
    // 尺寸限制: 最长边 ≤ 108英寸，长+宽+高 ≤ 130英寸
    // 但如果体积重量远大于实际重量，可能需要特殊处理
    const volume = longest * median * shortest;
    const volumetricWeight = volume / 139;

    // 特殊处理：如果体积重量超过40磅，即使总重量≤50磅，也归类为特殊超大件
    if (longest <= 108 && (longest + median + shortest) <= 130 && weightLbs <= 50 && volumetricWeight <= 40) {
      return 'large-oversize';
    }

    // 特殊超大尺寸 (Special Oversize)
    // 重量范围: > 50磅，分为多个子段
    // 尺寸限制: 长+宽+高 ≤ 165英寸，单边 ≤ 108英寸
    // 或者体积重量很大（超过40磅）
    if (weightLbs > 50 || longest > 108 || (longest + median + shortest) > 130 || volumetricWeight > 40) {
      return 'special-oversize';
    }

    // 默认超大尺寸
    return 'special-oversize';
  }

  /**
   * 确定价格分段
   */
  static determinePriceTier(price: number): PriceTier {
    if (price < 10) return 'low';
    if (price <= 50) return 'medium';
    return 'high';
  }

  /**
   * 计算体积重量 - Amazon FBA官方公式
   */
  static calculateVolumetricWeight(lengthIn: number, widthIn: number, heightIn: number): number {
    return (lengthIn * widthIn * heightIn) / 139; // 立方英寸 / 139 (Amazon官方标准)
  }

  /**
   * 计算基础FBA费用
   */
  static calculateBaseFee(
    sizeTier: SizeTier,
    priceTier: PriceTier,
    billingWeightLbs: number
  ): number {
    const fees = FBA_FEES_2026_NON_PEAK[sizeTier];
    if (!fees) return 0;

    const tierFees = fees.tiers[priceTier];
    if (!tierFees) return 0;

    // 标准尺寸按重量区间计算
    if (sizeTier === 'small-standard' || sizeTier === 'large-standard') {
      for (const weightBracket of tierFees) {
        if (billingWeightLbs <= weightBracket.maxWeight) {
          return weightBracket.fee;
        }
      }

      // 超过最大重量，按增加的重量计算
      const lastBracket = tierFees[tierFees.length - 1];
      const additionalWeight = billingWeightLbs - lastBracket.maxWeight;

      if (sizeTier === 'small-standard') {
        // 小号标准超过1磅，每0.25磅增加$0.21
        const additionalUnits = Math.ceil(additionalWeight / 0.25);
        return lastBracket.fee + 0.21 * additionalUnits;
      } else if (sizeTier === 'large-standard') {
        // 大号标准超过3磅，每0.25磅增加$0.08，基于Amazon官方文档
        const additionalUnits = Math.ceil(additionalWeight / 0.25);
        return lastBracket.fee + 0.08 * additionalUnits;
      }
    }

    // 大件商品计算
    if (sizeTier === 'small-oversize' || sizeTier === 'large-oversize') {
      const feeStructure = tierFees[0] as any;
      if (feeStructure?.additionalPerLb && billingWeightLbs > 1) {
        return feeStructure.fee + (billingWeightLbs - 1) * feeStructure.additionalPerLb;
      }
      return feeStructure?.fee || 0;
    }

    // 超大件商品分段计费
    if (sizeTier === 'special-oversize') {
      for (const weightSegment of tierFees) {
        if (billingWeightLbs <= weightSegment.maxWeight) {
          const segment = weightSegment as any;

          // 根据不同的重量段使用不同的计算方式
          if (segment.weightSegment === '0-50') {
            // 0-50磅段：基础费 + (重量-1) × 每磅费率
            return segment.fee + Math.max(0, billingWeightLbs - 1) * segment.additionalPerLb;
          } else if (segment.weightSegment === '50-70') {
            // 50-70磅段：基础费 + (重量-51) × 每磅费率
            return segment.fee + Math.max(0, billingWeightLbs - segment.baseWeight) * segment.additionalPerLb;
          } else if (segment.weightSegment === '70-150') {
            // 70-150磅段：基础费 + (重量-71) × 每磅费率
            return segment.fee + Math.max(0, billingWeightLbs - segment.baseWeight) * segment.additionalPerLb;
          } else if (segment.weightSegment === 'over-150') {
            // 超过150磅段：基础费 + (重量-151) × 每磅费率
            return segment.fee + Math.max(0, billingWeightLbs - segment.baseWeight) * segment.additionalPerLb;
          }
        }
      }
    }

    return 0;
  }

  /**
   * 主计算函数
   */
  static calculate(input: FbaCalculationInput): FbaCalculationResult {
    // 转换单位
    const dimensionsInInches = this.convertDimensionsToInches(input.dimensions);
    const weightInLbs = this.convertWeightToLbs(input.weight);

    // 确定分段
    const sizeTier = this.determineSizeTier(
      dimensionsInInches.length,
      dimensionsInInches.width,
      dimensionsInInches.height,
      weightInLbs
    );

    const priceTier = this.determinePriceTier(input.price);

    // 计算体积重量
    const volumetricWeight = this.calculateVolumetricWeight(
      dimensionsInInches.length,
      dimensionsInInches.width,
      dimensionsInInches.height
    );

    // 确定计费重量 - Amazon FBA官方规则
    let billingWeight: number;

    // 小号标准尺寸：仅使用实际重量，但仍需进位到0.25磅
    if (sizeTier === 'small-standard') {
      billingWeight = weightInLbs;
      // 进位规则：向上取整到最近的0.25磅
      billingWeight = Math.ceil(billingWeight * 4) / 4;
    }
    // 超过150磅超大件：仅使用实际重量，但仍需进位到0.25磅
    else if (sizeTier === 'special-oversize' && weightInLbs > 150) {
      billingWeight = weightInLbs;
      // 进位规则：向上取整到最近的0.25磅
      billingWeight = Math.ceil(billingWeight * 4) / 4;
    }
    // 其他所有商品：max(实际重量, 体积重量)
    else {
      billingWeight = Math.max(weightInLbs, volumetricWeight);
      // 进位规则：向上取整到最近的0.25磅
      billingWeight = Math.ceil(billingWeight * 4) / 4;
    }

    // 计算基础费用
    let baseFee = this.calculateBaseFee(sizeTier, priceTier, billingWeight);

    // 应用服装类调整
    const isApparel = input.productType === 'apparel';
    if (isApparel) {
      baseFee *= APPAREL_ADJUSTMENTS[sizeTier];
    }

    // 计算调整费用
    const isHazardous = input.productType === 'hazardous';
    const isLowPrice = priceTier === 'low';
    const isPeakSeason = input.isPeakSeason;

    // 危险品附加费：基础费用的30%
    const hazardousFee = isHazardous ? baseFee * 0.3 : 0;

    // 假日旺季费用：2025年10月15日-2026年1月14日，比非旺季高约$0.35-$0.50
    // 使用固定增量计算，更准确反映Amazon实际费用结构
    const peakSeasonFee = isPeakSeason ? Math.min(0.50, Math.max(0.35, baseFee * 0.05)) : 0;

    // 低价配送费用优惠：售价<$10商品平均节省$0.86
    const lowPriceDiscount = isLowPrice ? -0.86 : 0;

    // 计算尺寸信息
    const sortedDimensions = [
      dimensionsInInches.length,
      dimensionsInInches.width,
      dimensionsInInches.height
    ].sort((a, b) => b - a);

    const [longestSide, medianSide, shortestSide] = sortedDimensions;
    const lengthPlusGirth = longestSide + 2 * (medianSide + shortestSide);

    // 特大号处理费：2026年1月15日起生效
    // 触发条件：最长边 > 96英寸 或 长度+围长 > 130英寸
    // 仅适用于超大件商品(≤150磅)
    let specialHandlingFee = 0;
    if (sizeTier === 'special-oversize' && weightInLbs <= 150) {
      if (longestSide > 96 || lengthPlusGirth > 130) {
        // 根据FBA规则文档，特大号处理费为固定费用
        // 具体费用需要查证官方费率表，待Amazon发布2026年费率后更新
        specialHandlingFee = 0; // 待补充官方费率
      }
    }

    // 计算总费用 - Amazon FBA最小费用为$2.43
    let totalFee = baseFee + hazardousFee + peakSeasonFee + lowPriceDiscount + specialHandlingFee;
    totalFee = Math.max(2.43, totalFee); // FBA费用最小值

    return {
      sizeTier,
      priceTier,
      baseFee,
      hazardousFee,
      peakSeasonFee,
      lowPriceDiscount,
      specialHandlingFee,
      totalFee,
      billingWeight: {
        actual: weightInLbs,
        volumetric: volumetricWeight,
        used: billingWeight
      },
      dimensions: {
        longestSide,
        medianSide,
        shortestSide,
        lengthPlusGirth
      },
      modifiers: {
        isApparel,
        isHazardous,
        isPeakSeason,
        isLowPrice
      }
    };
  }
}